import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, RefreshCw, Copy, Eye, EyeOff } from 'lucide-react';

interface DeveloperApp {
    id: number;
    name: string;
    description: string | null;
    client_id: string;
    redirect_uri: string;
    website_url: string | null;
    scopes: string[];
    is_approved: boolean;
    is_active: boolean;
    extensions_count: number;
    created_at: string;
}

export default function DeveloperPage() {
    const [apps, setApps] = useState<DeveloperApp[]>([]);
    const [scopes, setScopes] = useState<string[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [newSecret, setNewSecret] = useState<string | null>(null);
    const [showSecret, setShowSecret] = useState(false);
    const [form, setForm] = useState({
        name: '',
        description: '',
        redirect_uri: '',
        website_url: '',
        scopes: [] as string[],
    });

    const fetchApps = () => {
        api.get<{ data: DeveloperApp[] }>('/developer/apps').then((r) => setApps(r.data));
    };

    useEffect(() => {
        fetchApps();
        api.get<{ data: string[] }>('/developer/scopes').then((r) => setScopes(r.data));
    }, []);

    const handleCreate = async () => {
        const res = await api.post<{ data: DeveloperApp; client_secret: string }>('/developer/apps', form);
        setNewSecret(res.client_secret);
        setForm({ name: '', description: '', redirect_uri: '', website_url: '', scopes: [] });
        setShowForm(false);
        fetchApps();
    };

    const handleDelete = async (id: number) => {
        await api.delete(`/developer/apps/${id}`);
        fetchApps();
    };

    const handleRegenerateSecret = async (id: number) => {
        const res = await api.post<{ client_secret: string }>(`/developer/apps/${id}/regenerate-secret`);
        setNewSecret(res.client_secret);
    };

    const toggleScope = (scope: string) => {
        setForm((f) => ({
            ...f,
            scopes: f.scopes.includes(scope)
                ? f.scopes.filter((s) => s !== scope)
                : [...f.scopes, scope],
        }));
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Developer Portal</h1>
                    <p className="text-sm text-slate-400">Create apps and extensions for DebtBound</p>
                </div>
                <Button size="sm" onClick={() => setShowForm(!showForm)}>
                    <Plus className="mr-1 h-4 w-4" />
                    New App
                </Button>
            </div>

            {newSecret && (
                <Card className="border-amber-700/50">
                    <CardContent className="p-4">
                        <p className="mb-2 text-sm font-medium text-amber-400">
                            Save your client secret now - it won't be shown again!
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 rounded bg-slate-800 px-3 py-2 text-xs font-mono">
                                {showSecret ? newSecret : '••••••••••••••••••••••••••••••••'}
                            </code>
                            <Button variant="ghost" size="sm" onClick={() => setShowSecret(!showSecret)}>
                                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newSecret)}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                        <Button variant="outline" size="sm" className="mt-2" onClick={() => setNewSecret(null)}>
                            Dismiss
                        </Button>
                    </CardContent>
                </Card>
            )}

            {showForm && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Register New App</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-medium">App Name</label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="My Extension"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium">Website URL</label>
                                <Input
                                    value={form.website_url}
                                    onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
                                    placeholder="https://myapp.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">Description</label>
                            <textarea
                                value={form.description}
                                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                className="flex min-h-[60px] w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                                rows={2}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">Redirect URI</label>
                            <Input
                                value={form.redirect_uri}
                                onChange={(e) => setForm((f) => ({ ...f, redirect_uri: e.target.value }))}
                                placeholder="https://myapp.com/callback"
                            />
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium">Scopes</label>
                            <div className="flex flex-wrap gap-2">
                                {scopes.map((scope) => (
                                    <button
                                        key={scope}
                                        type="button"
                                        onClick={() => toggleScope(scope)}
                                        className={`rounded-full px-3 py-1 text-xs transition ${
                                            form.scopes.includes(scope)
                                                ? 'bg-violet-600 text-white'
                                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                        }`}
                                    >
                                        {scope}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleCreate} disabled={!form.name || !form.redirect_uri || form.scopes.length === 0}>
                                Register App
                            </Button>
                            <Button variant="outline" onClick={() => setShowForm(false)}>
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {apps.length === 0 && !showForm && (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-500">
                        No apps registered yet. Create one to start building extensions.
                    </CardContent>
                </Card>
            )}

            {apps.map((app) => (
                <Card key={app.id}>
                    <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-medium">{app.name}</h3>
                                    <Badge variant={app.is_approved ? 'default' : 'secondary'} className="text-xs">
                                        {app.is_approved ? 'Approved' : 'Pending'}
                                    </Badge>
                                    {app.extensions_count > 0 && (
                                        <Badge variant="outline" className="text-xs">
                                            {app.extensions_count} extension{app.extensions_count !== 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                </div>
                                {app.description && (
                                    <p className="mt-1 text-sm text-slate-400">{app.description}</p>
                                )}
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs text-slate-500">Client ID:</span>
                                    <code className="text-xs text-slate-300">{app.client_id}</code>
                                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => copyToClipboard(app.client_id)}>
                                        <Copy className="h-3 w-3" />
                                    </Button>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {app.scopes.map((s) => (
                                        <span key={s} className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleRegenerateSecret(app.id)} title="Regenerate secret">
                                    <RefreshCw className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(app.id)} className="text-red-400 hover:text-red-300">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
